import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { LucideIcon } from 'lucide-react';
import { ChevronDown, Square } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getScrollableAncestors } from './institution-select-portal';

export type InstitutionStyledSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
  /** Icône optionnelle (ex. rôles collaborateurs). Sinon pastille neutre. */
  icon?: LucideIcon;
};

export type InstitutionStyledSelectProps = {
  id?: string;
  label?: ReactNode;
  hint?: ReactNode;
  options: InstitutionStyledSelectOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  /** z-index du panneau (tiroirs / modales : ex. z-[200]) */
  panelZIndexClass?: string;
};

function IconCell({ Icon, selected }: { Icon?: LucideIcon; selected: boolean }) {
  const I = Icon ?? Square;
  return (
    <span
      className={cn(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border',
        selected
          ? 'border-primary-200 bg-primary-50 text-primary-600 dark:border-primary-800/60 dark:bg-primary-950/50 dark:text-primary-300'
          : 'border-transparent bg-white text-[#5B6472] dark:bg-[#122644]/80 dark:text-[#B6CAEC]',
      )}
    >
      <I className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
    </span>
  );
}

type MenuCoords = { top: number; left: number; width: number; maxHeight: number };

/**
 * Liste déroulante alignée sur le sélecteur de rôle de la fiche collaborateur (designers) :
 * déclencheur « carte » + panneau options avec pastille de sélection.
 * Le panneau est rendu en portail (`document.body`) pour ne pas être rogné par `overflow` des tableaux / cartes.
 */
export function InstitutionStyledSelect({
  id: idProp,
  label,
  hint,
  options,
  value,
  onChange,
  disabled,
  className,
  panelZIndexClass = 'z-[120]',
}: InstitutionStyledSelectProps) {
  const genId = useId();
  const baseId = idProp ?? `inst-select-${genId.replace(/:/g, '')}`;
  const labelId = `${baseId}-label`;
  const listboxId = `${baseId}-listbox`;
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [menuCoords, setMenuCoords] = useState<MenuCoords | null>(null);

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);
  const selectedLabel = selected?.label ?? value;

  const syncMenuPosition = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const margin = 8;
    const gap = 4;
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const preferredMax = Math.min(280, vh * 0.45);
    const belowTop = r.bottom + gap;
    const spaceBelow = vh - belowTop - margin;
    const spaceAbove = r.top - margin - gap;
    /** Ouvre au-dessus si peu de place en bas (ex. champ en bas d’un panneau scrollable / mobile). */
    const openUp = spaceBelow < 140 && spaceAbove > spaceBelow;
    let top: number;
    let maxHeight: number;
    if (openUp) {
      maxHeight = Math.max(120, Math.min(preferredMax, spaceAbove));
      top = Math.max(margin, r.top - gap - maxHeight);
    } else {
      top = belowTop;
      maxHeight = Math.max(120, Math.min(preferredMax, spaceBelow));
    }
    const width = Math.min(r.width, vw - margin * 2);
    const left = Math.min(Math.max(margin, r.left), vw - width - margin);
    setMenuCoords({ top, left, width, maxHeight });
  }, []);

  useLayoutEffect(() => {
    if (!open || disabled) {
      setMenuCoords(null);
      return;
    }
    syncMenuPosition();
    const btn = triggerRef.current;
    if (!btn) return;
    const scrollParents = getScrollableAncestors(btn);
    const opts = { capture: true } as const;
    scrollParents.forEach((p) => p.addEventListener('scroll', syncMenuPosition, opts));
    window.addEventListener('resize', syncMenuPosition);
    return () => {
      scrollParents.forEach((p) => p.removeEventListener('scroll', syncMenuPosition, opts));
      window.removeEventListener('resize', syncMenuPosition);
    };
  }, [open, disabled, syncMenuPosition]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = useCallback(
    (v: string) => {
      onChange(v);
      setOpen(false);
    },
    [onChange],
  );

  if (options.length === 0) {
    return null;
  }

  const listbox =
    open && !disabled && menuCoords ? (
      <ul
        ref={menuRef}
        id={listboxId}
        role="listbox"
        aria-labelledby={label != null && label !== false ? labelId : undefined}
        style={{
          position: 'fixed',
          top: menuCoords.top,
          left: menuCoords.left,
          width: menuCoords.width,
          maxHeight: menuCoords.maxHeight,
          margin: 0,
        }}
        className={cn(
          'flex min-h-0 list-none flex-col gap-1 overflow-y-auto overscroll-contain rounded-xl border border-[#E8EDF5]/90 bg-[#F8FAFC]/95 p-1.5 shadow-lg ring-1 ring-black/5 dark:border-[#B6CAEC] dark:bg-[#0C1C38]/95 dark:ring-white/10',
          panelZIndexClass,
        )}
      >
        {options.map((o) => {
          const isSel = value === o.value;
          return (
            <li key={o.value} role="presentation" className="list-none">
              <button
                type="button"
                role="option"
                aria-selected={isSel}
                disabled={o.disabled}
                onClick={() => !o.disabled && pick(o.value)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-start text-sm transition-all',
                  isSel
                    ? 'bg-white text-primary-900 shadow-sm ring-2 ring-primary-500/35 dark:bg-[#122644] dark:text-primary-50 dark:ring-primary-400/40'
                    : 'text-[#121826] hover:bg-white/90 hover:shadow-sm dark:text-[#DCE7F7] dark:hover:bg-[#122644]/90',
                  o.disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                <IconCell Icon={o.icon} selected={isSel} />
                <span className="min-w-0 flex-1 font-medium leading-snug">{o.label}</span>
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                    isSel
                      ? 'border-primary-600 bg-primary-900 dark:border-primary-400 dark:bg-primary-400'
                      : 'border-[#E8EDF5] bg-transparent dark:border-[#5C82C5]',
                  )}
                  aria-hidden
                >
                  {isSel ? <span className="h-1.5 w-1.5 rounded-full bg-white dark:bg-[#0C1C38]" /> : null}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    ) : null;

  return (
    <div className={cn('w-full', className)}>
      {label != null && label !== false ? (
        <label htmlFor={baseId} id={labelId} className="mb-1.5 block text-xs font-medium text-[#121826] dark:text-[#DCE7F7]">
          {label}
        </label>
      ) : null}
      <div ref={containerRef} className="relative">
        <button
          ref={triggerRef}
          type="button"
          id={baseId}
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-labelledby={label != null && label !== false ? labelId : undefined}
          aria-label={label == null || label === false ? String(selectedLabel) : undefined}
          onClick={() => !disabled && setOpen((v) => !v)}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg border px-2.5 py-2 text-start text-sm transition-all',
            'border-[#E8EDF5]/90 bg-[#F8FAFC]/80 dark:border-[#B6CAEC] dark:bg-[#0C1C38]/50',
            'bg-white text-primary-900 shadow-sm ring-2 ring-primary-500/35 dark:bg-[#122644] dark:text-primary-50 dark:ring-primary-400/40',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <IconCell Icon={selected?.icon} selected />
          <span className="min-w-0 flex-1 font-medium leading-snug">{selectedLabel}</span>
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-[#5B6472] transition-transform dark:text-[#B6CAEC]', open && 'rotate-180')}
            aria-hidden
          />
        </button>
      </div>
      {typeof document !== 'undefined' && listbox ? createPortal(listbox, document.body) : null}
      {hint ? <p className="mt-1 text-[11px] text-[#5B6472] dark:text-[#B6CAEC]">{hint}</p> : null}
    </div>
  );
}
